import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormArrayGroup } from '@shared/components/form-array-group/model/form-array-group';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { SelectModel } from '@shared/components/select/models/select';
import { takeUntil, tap } from 'rxjs/operators';
import { CustomIcons } from '@shared/models/custom-icons';
import { CatalogItemFieldsVM, CatalogView } from '@shared/models/shared-catalog.state';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AddressService } from '@shared/components/map/services/address.service';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { MapComponent } from '@shared/components/map/components/map/map.component';
import { AppealsService } from '@shared/components/appeals/services/appeals.service';
import { whitespaceValidator } from '@utils/form-validators';

@Component({
  selector: 'app-new-object-form',
  templateUrl: './new-object-form.component.html',
  styleUrls: ['./new-object-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class NewObjectFormComponent implements OnInit {
  public componentDestroy: () => Subject<any>;
  @ViewChild(MapComponent) public mapComponent: MapComponent;
  @Input() public appealId: number | null;
  @Output() public readonly formInit = new EventEmitter<UntypedFormGroup>();
  @Input() public showMap: boolean;
  @Input() public isImpaired: boolean;
  @Select(SharedCatalogState.selectCatalog) public selectCatalog$: Observable<CatalogView>;
  public itemFields: SelectModel[] = [];
  public itemFields$: Observable<CatalogItemFieldsVM[] | undefined>;
  public icons = CustomIcons;
  public form: UntypedFormGroup = new UntypedFormGroup({
    nameCatalog: new UntypedFormControl({ value: '', disabled: true }, [Validators.required, whitespaceValidator]),
    nameItem: new UntypedFormControl('', [Validators.required, whitespaceValidator]),
    address: new UntypedFormControl('', whitespaceValidator),
    comment: new UntypedFormControl('', [Validators.required, whitespaceValidator]),
    coordinateX: new UntypedFormControl('', whitespaceValidator),
    coordinateY: new UntypedFormControl('', whitespaceValidator),
    attributes: new UntypedFormArray([]),
    themeId: new UntypedFormControl(158),
    formType: new UntypedFormControl('new')
  });

  public attributesControl = this.form.controls.attributes as UntypedFormArray;
  public attributeGroupData: FormArrayGroup = {
    label: 'SHARED_COMPONENTS.FORM_ARRAY_GROUP.LABEL_ATTRIBUTE',
    buttonLabel: 'SHARED_COMPONENTS.FORM_ARRAY_GROUP.BUTTON_LABEL',
    mainForm: this.form,
    formArray: this.attributesControl,
    currentFormGroup: () => this.attributeFromGroup(),
    formGroupName: 'attributes',
    placeholderSelectorName: 'SHARED_COMPONENTS.FORM_ARRAY_GROUP.PLACEHOLDER_SELECTOR_ATTRIBUTE',
    formControlSelectorName: 'attribute',
    placeholderValue: 'SHARED_COMPONENTS.FORM_ARRAY_GROUP.ENTER_ATTRIBUTE_VALUE',
    formControlValueName: 'value'
  };
  public showMobileMap = false;
  public addressValue$: Observable<string>;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(tap(() => (this.showMobileMap = false)));
  public resetMap$: Observable<boolean>;

  constructor(
    private store: Store,
    private breakpointObserver: BreakpointObserver,
    private addressService: AddressService,
    @Inject(PLATFORM_ID) private platformId: string,
    private snackBar: SnackBarService,
    private appealService: AppealsService,
    private cdr: ChangeDetectorRef
  ) {}

  private attributeFromGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      attribute: new UntypedFormControl(''),
      value: new UntypedFormControl('')
    });
  }

  public ngOnInit(): void {
    this.resetMap$ = this.appealService.resetMap.pipe(
      tap(isResetMap => {
        if (isResetMap) {
          this.cdr.detectChanges();
          setTimeout(() => {
            if (this.mapComponent) {
              this.mapComponent.removeAllObjectFromMap();
            }
          });
        }
      })
    );
    this.formInit.emit(this.form);
    this.addressValue$ = this.form.controls.address.valueChanges;
    this.itemFields$ = this.store.select(SharedCatalogState.fields).pipe(
      tap(fields => {
        this.itemFields =
          fields?.map(f => ({
            value: f.name,
            title: f.name
          })) ?? [];
      })
    );
  }

  public setCoordinates($event: number[]): void {
    this.form.controls.coordinateX.setValue($event[0]);
    this.form.controls.coordinateY.setValue($event[1]);

    this.addressService
      .getLocationByCoords($event[0], $event[1])
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe(location => {
        if (location?.data[0]?.name) {
          this.form.controls.address.setValue(location.data[0].name);
        } else {
          this.snackBar.open(MirSnackbarType.Error, [
            {
              translate: true,
              message: 'SNACK_BAR_MESSAGE.MAP_POINT_LOADING_ERROR'
            }
          ]);
        }
      });
  }

  public cancelMobileMap(): void {
    this.showMobileMap = false;
    this.form.controls.address.setValue('');
  }

  public confirm(): void {
    this.showMobileMap = false;
  }
}
